import React from "react"
import InvalidArgumentsError from "src/classes/InvalidArgumentsError"
import get from "lodash/get"
import NotFound from "src/components/pages/NotFound"
import PropTypes from "prop-types"
import hocInterface from "src/helpers/hocInterface"
import pick from "ramda/es/pick"

const isOnlyDigits = str => /^[0-9]+$/.test(String(str))
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const isValidUuid = str => UUID_REGEX.test(str)

const validateHOCConfig = config => {
  if (!config || !Array.isArray(config)) {
    throw new InvalidArgumentsError(`withIdRouteValidation expects array as
     an argument, you provided ${config}`)
  } else {
    config.forEach(typeLocationPair => {
      const { type, index } = typeLocationPair
      if (![`id`, `uuid`].includes(type)) {
        throw new InvalidArgumentsError(`type of route parameter to validate
        should be either "id" or "uuid", you provided "${type}"
        `)
      }

      if (!index || !isOnlyDigits(index)) {
        throw new InvalidArgumentsError(`type of route parameter to validate
        should be positive integer, you provided "${index}"
        `)
      }
    })
  }
}

/**
 * @function
 * @description Allows to wrap a component with a HOC that validates
 * id or uuid part of the route provided by react-router
 * @param {{
 *   routeValidationConfig: Array.<{type: ( 'id' | 'uuid'), index: Number }>
 *   isSideModal: boolean,
 *    }} config of a HOC
 * @returns {function} a HOC
 */
const withRouteValidation = ({ routeValidationConfig, isSideModal = false }) =>
  hocInterface(
    {
      injectProps: {
        history: PropTypes.shape({
          location: PropTypes.shape({ pathname: PropTypes.string }),
        }),
      },
      exposeProps: pick([`isValidId`]),
    },
    BaseComponent => props => {
      validateHOCConfig(routeValidationConfig)

      // taken from react-router
      const routeStr = get(props, `history.location.pathname`)

      const routeArr = routeStr.split(`/`)
      // remove first element (empty string)
      routeArr.shift()

      let isValidId = true
      routeValidationConfig.forEach(({ type, index }) => {
        const idPart = routeArr[index]
        if (idPart === `new`) {
          return
        }
        if (type === `id` && !isOnlyDigits(idPart)) {
          isValidId = false
        } else if (type === `uuid` && !isValidUuid(idPart)) {
          isValidId = false
        }
      })

      if (isSideModal) {
        return <BaseComponent {...props} isValidId={isValidId} />
      }

      return isValidId ? (
        <BaseComponent {...props} isValidId={isValidId} />
      ) : (
        <NotFound />
      )
    },
  )

export default withRouteValidation
